<template>
    <div class="page-container">
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <CrmPageHeader
            title="Roles y permisos"
            subtitle="Qué puede hacer cada tipo de usuario en el CRM"
            icon="fas fa-user-shield"
            icon-bg="linear-gradient(135deg,#4C1D95,#8B5CF6)"
        >
            <soft-button v-if="tab === 'roles'" variant="gradient" color="primary" size="sm" @click="abrirCrearRol">
                <i class="fas fa-plus me-1"></i> Nuevo rol
            </soft-button>
            <soft-button v-else-if="tab === 'modulos'" variant="gradient" color="primary" size="sm" @click="abrirModuloForm()">
                <i class="fas fa-plus me-1"></i> Nuevo módulo
            </soft-button>
            <soft-button v-else variant="gradient" color="primary" size="sm" @click="abrirAccionForm()">
                <i class="fas fa-plus me-1"></i> Nueva acción
            </soft-button>
        </CrmPageHeader>

        <div v-if="esSuperAdmin" class="tabs mb-4">
            <button type="button" class="tabs__item" :class="{ 'tabs__item--activa': tab === 'roles' }" @click="cambiarTab('roles')">
                <i class="fas fa-user-tag me-1"></i> Roles
            </button>
            <button type="button" class="tabs__item" :class="{ 'tabs__item--activa': tab === 'modulos' }" @click="cambiarTab('modulos')">
                <i class="fas fa-cubes me-1"></i> Módulos
            </button>
            <button type="button" class="tabs__item" :class="{ 'tabs__item--activa': tab === 'acciones' }" @click="cambiarTab('acciones')">
                <i class="fas fa-bolt me-1"></i> Acciones
            </button>
        </div>

        <!-- Roles -->
        <template v-if="tab === 'roles'">
            <div class="info-box mb-4">
                <i class="fas fa-info-circle"></i>
                <div v-if="esSuperAdmin">
                    Estás viendo los roles <strong>públicos</strong>: los usan todas las inmobiliarias.
                    Los cambios de permisos aplican cuando cada usuario recarga o vuelve a iniciar sesión.
                </div>
                <div v-else>
                    Roles de tu inmobiliaria. Un agente nunca tiene un permiso que su administrador no tenga,
                    aunque se lo marques aquí.
                </div>
            </div>

            <div class="crm-card">
                <div class="toolbar">
                    <input
                        v-model="busqueda"
                        type="text"
                        class="form-control form-control-sm toolbar__buscar"
                        placeholder="Buscar en esta página"
                    />
                    <span class="text-xs text-secondary">{{ roles.total }} rol(es)</span>
                </div>

                <div class="table-responsive">
                    <table class="tabla" :class="{ 'is-loading': roles.loading }">
                        <thead>
                            <tr>
                                <th>Rol</th>
                                <th>Tipo</th>
                                <th>Permisos</th>
                                <th class="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="rol in rolesFiltrados" :key="rol.id">
                                <td>
                                    <div class="rol__nombre">
                                        {{ rol.nombre }}
                                        <span class="text-secondary text-xs ms-1">#{{ rol.id }}</span>
                                    </div>
                                    <div v-if="textoPlano(rol.descripcion)" class="rol__desc">{{ textoPlano(rol.descripcion) }}</div>
                                    <code class="rol__slug">{{ rol.slug }}</code>
                                </td>
                                <td>
                                    <span v-if="Number(rol.is_super_admin)" class="chip chip--danger">Superadmin</span>
                                    <span v-else-if="Number(rol.is_main)" class="chip chip--info">Dueño de cuenta</span>
                                    <span v-else class="chip chip--muted">Agente</span>
                                    <span class="chip ms-1" :class="rol.type === 'public' ? 'chip--ok' : 'chip--muted'">
                                        {{ rol.type === 'public' ? 'Público' : 'Privado' }}
                                    </span>
                                </td>
                                <td>
                                    <span class="text-sm">
                                        <strong>{{ resumenPermisos(rol).modulos }}</strong> módulo(s) ·
                                        <strong>{{ resumenPermisos(rol).acciones }}</strong> acción(es)
                                    </span>
                                </td>
                                <td class="text-end text-nowrap">
                                    <button type="button" class="icon-btn me-1" title="Permisos" @click="abrirMatriz(rol)">
                                        <i class="fas fa-th"></i>
                                    </button>
                                    <button type="button" class="icon-btn" title="Editar" @click="abrirEditarRol(rol)">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="roles.loading && !roles.items.length">
                                <td colspan="4" class="tabla__hint"><i class="fas fa-spinner fa-spin me-1"></i> Cargando…</td>
                            </tr>
                            <tr v-else-if="!rolesFiltrados.length">
                                <td colspan="4" class="tabla__hint">
                                    {{ busqueda ? 'Ningún rol de esta página coincide' : 'No hay roles' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="roles.lastPage > 1" class="d-flex justify-content-end p-3">
                    <base-pagination
                        :current-page="roles.page"
                        :pageCount="roles.lastPage"
                        @nextPage="cambiarPagina('next')"
                        @previousPage="cambiarPagina('previous')"
                        @loadPage="cambiarPagina"
                    />
                </div>
            </div>
        </template>

        <!-- Modulos -->
        <template v-else-if="tab === 'modulos'">
            <div class="warn-box mb-4">
                <i class="fas fa-exclamation-triangle"></i>
                <div>
                    El <strong>subject</strong> es el nombre que usa el código del CRM. No se puede cambiar una vez creado:
                    hacerlo rompería los permisos de esa sección para todos.
                </div>
            </div>

            <div class="crm-card">
                <div class="toolbar">
                    <input
                        v-model="modulos.filtro"
                        type="text"
                        class="form-control form-control-sm toolbar__buscar"
                        placeholder="Buscar módulo o subject"
                    />
                    <span class="text-xs text-secondary">{{ modulos.items.length }} módulo(s)</span>
                </div>

                <div class="table-responsive">
                    <table class="tabla" :class="{ 'is-loading': modulos.loading }">
                        <thead>
                            <tr>
                                <th>Módulo</th>
                                <th>Acciones que ofrece</th>
                                <th class="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="modulo in modulosFiltrados" :key="modulo.id">
                                <td>
                                    <div class="rol__nombre">
                                        {{ modulo.title }}
                                        <span class="text-secondary text-xs ms-1">#{{ modulo.id }}</span>
                                    </div>
                                    <code class="rol__slug">{{ modulo.subject }}</code>
                                    <span v-if="modulo.type && modulo.type !== 'public'" class="chip chip--muted ms-1">{{ modulo.type }}</span>
                                </td>
                                <td>
                                    <div class="acciones-chips">
                                        <span v-for="a in accionesOfrecidas(modulo.id)" :key="a.id" class="chip chip--muted">
                                            {{ accionLabel(a.id, a.title) }}
                                        </span>
                                        <span v-if="!accionesOfrecidas(modulo.id).length" class="text-xs text-secondary">
                                            {{ modulo.type && modulo.type !== 'public' ? 'No aparece en la matriz (no es público)' : 'Sin acciones: no aparece en la matriz' }}
                                        </span>
                                    </div>
                                </td>
                                <td class="text-end text-nowrap">
                                    <button type="button" class="icon-btn me-1" title="Acciones que ofrece" @click="abrirOfrecidas(modulo)">
                                        <i class="fas fa-tasks"></i>
                                    </button>
                                    <button type="button" class="icon-btn" title="Editar" @click="abrirModuloForm(modulo)">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="modulos.loading && !modulos.items.length">
                                <td colspan="3" class="tabla__hint"><i class="fas fa-spinner fa-spin me-1"></i> Cargando…</td>
                            </tr>
                            <tr v-else-if="!modulosFiltrados.length">
                                <td colspan="3" class="tabla__hint">Sin resultados</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <!-- Acciones -->
        <template v-else>
            <div class="warn-box mb-4">
                <i class="fas fa-exclamation-triangle"></i>
                <div>
                    El <strong>código</strong> de la acción lo usa el CRM (por ejemplo <code>updated</code>, no <code>update</code>).
                    No se puede cambiar una vez creada.
                </div>
            </div>

            <div class="crm-card">
                <div class="toolbar">
                    <input
                        v-model="accionesFiltro"
                        type="text"
                        class="form-control form-control-sm toolbar__buscar"
                        placeholder="Buscar acción o código"
                    />
                    <span class="text-xs text-secondary">{{ acciones.length }} acción(es)</span>
                </div>

                <div class="table-responsive">
                    <table class="tabla" :class="{ 'is-loading': loadingCatalogo }">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>Nombre</th>
                                <th>Código</th>
                                <th class="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="accion in accionesFiltradas" :key="accion.id">
                                <td class="text-secondary text-sm">#{{ accion.id }}</td>
                                <td class="text-sm">{{ accion.title || '—' }}</td>
                                <td><code class="rol__slug">{{ accion.action }}</code></td>
                                <td class="text-end">
                                    <button type="button" class="icon-btn" title="Editar" @click="abrirAccionForm(accion)">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="!accionesFiltradas.length">
                                <td colspan="4" class="tabla__hint">Sin resultados</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <!-- Crear / editar rol -->
        <soft-modal :modal-active="rolForm.active" size="md" @close="cerrarRolForm">
            <template #header>{{ rolForm.id ? 'Editar rol' : 'Nuevo rol' }}</template>

            <div v-if="rolForm.error" class="form-error mb-3">{{ rolForm.error }}</div>

            <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input
                    v-model="rolForm.nombre"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': rolForm.errores.nombre }"
                    maxlength="191"
                    @keyup.enter="guardarRol"
                />
                <div v-if="rolForm.errores.nombre" class="invalid-feedback">{{ rolForm.errores.nombre }}</div>
                <div v-else-if="nombreRepetido" class="warn-box warn-box--sm mt-2">
                    <i class="fas fa-exclamation-triangle"></i>
                    <div>
                        Ya existe el rol <strong>{{ nombreRepetido.nombre }}</strong> (#{{ nombreRepetido.id }}).
                        Con el mismo nombre, los usuarios de ambos roles reciben la suma de los permisos de los dos.
                    </div>
                </div>
                <p v-if="rolForm.id" class="text-xs text-secondary mt-1 mb-0">
                    Al cambiar el nombre también cambia el identificador (slug) del rol.
                </p>
            </div>

            <div class="mb-3">
                <label class="form-label">Descripción <span class="text-secondary">(opcional)</span></label>
                <textarea v-model="rolForm.descripcion" class="form-control" rows="3"></textarea>
                <p v-if="rolForm.descripcionHtml" class="text-xs text-secondary mt-1 mb-0">
                    La descripción tenía formato del CRM anterior; se guardará como texto plano.
                </p>
            </div>

            <div v-if="esSuperAdmin" class="padre-box">
                <div class="form-check form-switch mb-1">
                    <input id="rolIsMain" v-model="rolForm.is_main" class="form-check-input" type="checkbox" />
                    <label class="form-check-label text-sm" for="rolIsMain">Rol de dueño de cuenta (administrador de inmobiliaria)</label>
                </div>
                <p class="text-xs text-secondary mb-0">
                    Actívalo solo para roles de dueño. En un rol de agente hace que cada agente se trate como
                    su propia inmobiliaria y deje de ver los datos de su oficina.
                </p>
            </div>

            <p v-if="!rolForm.id" class="text-xs text-secondary mt-3 mb-0">
                <i class="fas fa-info-circle me-1"></i>
                Al crearlo solo tendrá acceso al perfil; después marcas sus permisos.
            </p>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarRolForm">Cancelar</soft-button>
                <soft-button variant="gradient" color="primary" size="sm" :loading="rolForm.guardando" :disabled="rolForm.guardando" @click="guardarRol">
                    {{ rolForm.id ? 'Guardar cambios' : 'Crear y asignar permisos' }}
                </soft-button>
            </template>
        </soft-modal>

        <!-- Matriz de permisos -->
        <soft-modal :modal-active="matriz.active" size="xl" @close="cerrarMatriz">
            <template #header>
                <i class="fas fa-th me-2"></i>Permisos de
                <strong>{{ matriz.rol && matriz.rol.nombre }}</strong>
            </template>

            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <input
                    v-model="matriz.filtro"
                    type="text"
                    class="form-control form-control-sm toolbar__buscar"
                    placeholder="Buscar módulo"
                />
                <span class="chip chip--info">{{ totalMarcadas }} acción(es) marcadas</span>
            </div>

            <div v-if="fueraDeCatalogo.length" class="warn-box warn-box--sm mb-3">
                <i class="fas fa-exclamation-triangle"></i>
                <div>
                    Este rol tiene acciones que el catálogo ya no ofrece y se quitarán al guardar:
                    {{ fueraDeCatalogo.join(', ') }}.
                </div>
            </div>

            <p v-if="loadingCatalogo && !catalogo.length" class="text-sm text-secondary">
                <i class="fas fa-spinner fa-spin me-1"></i> Cargando catálogo…
            </p>

            <div v-for="modulo in modulosMatriz" :key="modulo.id" class="modulo-row">
                <div class="modulo-row__head">
                    <span class="modulo-row__title">{{ modulo.title }}</span>
                    <span class="mini-badge">{{ marcadasDeModulo(modulo) }}/{{ modulo.actions.length }}</span>
                    <button type="button" class="link-btn ms-auto" @click="toggleModulo(modulo)">
                        {{ marcadasDeModulo(modulo) === modulo.actions.length ? 'Quitar todas' : 'Marcar todas' }}
                    </button>
                </div>
                <div class="modulo-row__acciones">
                    <label
                        v-for="accion in modulo.actions"
                        :key="accion.id"
                        class="accion-check"
                        :class="{ 'accion-check--on': estaMarcada(modulo.id, accion.id) }"
                    >
                        <input
                            type="checkbox"
                            :checked="estaMarcada(modulo.id, accion.id)"
                            @change="toggleAccion(modulo.id, accion.id)"
                        />
                        {{ accionLabel(accion.id, accion.title) }}
                    </label>
                </div>
            </div>

            <p v-if="!loadingCatalogo && !modulosMatriz.length" class="text-sm text-secondary mb-0">Sin resultados</p>

            <div v-if="matriz.error" class="form-error mt-3">{{ matriz.error }}</div>

            <template #footer>
                <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 w-100">
                    <span class="text-xs text-secondary">
                        Aplica cuando los usuarios del rol recarguen o vuelvan a iniciar sesión.
                    </span>
                    <div class="d-flex gap-2">
                        <soft-button variant="outline" color="secondary" size="sm" @click="cerrarMatriz">Cancelar</soft-button>
                        <soft-button
                            variant="gradient"
                            color="primary"
                            size="sm"
                            :loading="matriz.guardando"
                            :disabled="matriz.guardando || !catalogo.length"
                            @click="guardarMatriz"
                        >
                            Guardar permisos
                        </soft-button>
                    </div>
                </div>
            </template>
        </soft-modal>

        <!-- Crear / editar modulo -->
        <soft-modal :modal-active="moduloForm.active" size="sm" @close="cerrarModuloForm">
            <template #header>{{ moduloForm.id ? 'Editar módulo' : 'Nuevo módulo' }}</template>

            <div v-if="moduloForm.error" class="form-error mb-3">{{ moduloForm.error }}</div>

            <div class="mb-3">
                <label class="form-label">Nombre visible</label>
                <input
                    v-model="moduloForm.title"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': moduloForm.errores.title }"
                    @keyup.enter="guardarModulo"
                />
                <div v-if="moduloForm.errores.title" class="invalid-feedback">{{ moduloForm.errores.title }}</div>
            </div>

            <div class="mb-1">
                <label class="form-label">Subject</label>
                <input
                    v-model="moduloForm.subject"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': moduloForm.errores.subject }"
                    :disabled="!!moduloForm.id"
                    placeholder="ej: whatsapp"
                />
                <div v-if="moduloForm.errores.subject" class="invalid-feedback">{{ moduloForm.errores.subject }}</div>
                <p class="text-xs text-secondary mt-1 mb-0">
                    {{ moduloForm.id ? 'No se puede cambiar.' : 'Debe coincidir con el que usa el código del CRM. No se podrá cambiar.' }}
                </p>
            </div>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarModuloForm">Cancelar</soft-button>
                <soft-button variant="gradient" color="primary" size="sm" :loading="moduloForm.guardando" :disabled="moduloForm.guardando" @click="guardarModulo">
                    {{ moduloForm.id ? 'Guardar cambios' : 'Crear' }}
                </soft-button>
            </template>
        </soft-modal>

        <!-- Acciones que ofrece un modulo -->
        <soft-modal :modal-active="ofrecidas.active" size="md" @close="cerrarOfrecidas">
            <template #header>
                Acciones de <strong>{{ ofrecidas.modulo && ofrecidas.modulo.title }}</strong>
            </template>

            <p class="text-sm text-secondary">
                Las acciones que se podrán marcar para este módulo en la matriz de permisos.
            </p>

            <div class="ofrecidas-grid">
                <label
                    v-for="accion in acciones"
                    :key="accion.id"
                    class="accion-check"
                    :class="{ 'accion-check--on': ofrecidas.seleccion[accion.id] }"
                >
                    <input v-model="ofrecidas.seleccion[accion.id]" type="checkbox" />
                    <span>
                        {{ accion.title || accion.action }}
                        <code class="d-block text-xs">{{ accion.action }}</code>
                    </span>
                </label>
            </div>

            <p class="text-xs text-secondary mt-3 mb-0">
                <i class="fas fa-info-circle me-1"></i>
                Quitar una acción de aquí no la quita de los roles que ya la tienen activa.
            </p>

            <div v-if="ofrecidas.error" class="form-error mt-3">{{ ofrecidas.error }}</div>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarOfrecidas">Cancelar</soft-button>
                <soft-button
                    variant="gradient"
                    color="primary"
                    size="sm"
                    :loading="ofrecidas.guardando"
                    :disabled="ofrecidas.guardando || !idsOfrecidos.length"
                    @click="guardarOfrecidas"
                >
                    Guardar
                </soft-button>
            </template>
        </soft-modal>

        <!-- Crear / editar accion -->
        <soft-modal :modal-active="accionForm.active" size="sm" @close="cerrarAccionForm">
            <template #header>{{ accionForm.id ? 'Editar acción' : 'Nueva acción' }}</template>

            <div v-if="accionForm.error" class="form-error mb-3">{{ accionForm.error }}</div>

            <div class="mb-3">
                <label class="form-label">Nombre visible</label>
                <input
                    v-model="accionForm.title"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': accionForm.errores.title }"
                    @keyup.enter="guardarAccion"
                />
                <div v-if="accionForm.errores.title" class="invalid-feedback">{{ accionForm.errores.title }}</div>
            </div>

            <div class="mb-1">
                <label class="form-label">Código</label>
                <input
                    v-model="accionForm.action"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': accionForm.errores.action }"
                    :disabled="!!accionForm.id"
                    placeholder="ej: exportar_clientes"
                />
                <div v-if="accionForm.errores.action" class="invalid-feedback">{{ accionForm.errores.action }}</div>
                <p class="text-xs text-secondary mt-1 mb-0">
                    {{ accionForm.id ? 'No se puede cambiar.' : 'Debe coincidir con el que usa el código del CRM. No se podrá cambiar.' }}
                </p>
            </div>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarAccionForm">Cancelar</soft-button>
                <soft-button variant="gradient" color="primary" size="sm" :loading="accionForm.guardando" :disabled="accionForm.guardando" @click="guardarAccion">
                    {{ accionForm.id ? 'Guardar cambios' : 'Crear' }}
                </soft-button>
            </template>
        </soft-modal>
    </div>
</template>

<script>
import { onMounted } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import SoftModal from "@/components/SoftModal.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import BasePagination from "@/components/pagination/BasePagination.vue";
import { useRoles } from "./useRoles.js";

export default {
    name: "Roles",
    components: {
        SoftButton,
        SoftAlert,
        SoftModal,
        CrmPageHeader,
        BasePagination,
    },
    setup() {
        const roles = useRoles();
        onMounted(roles.iniciar);
        return { ...roles };
    },
};
</script>

<style scoped>
.info-box,
.warn-box {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 0.8rem;
    line-height: 1.45;
}

.info-box { background: #EFF6FF; border: 1px solid #BFDBFE; color: #1E3A8A; }
.warn-box { background: #FFFBEB; border: 1px solid #FDE68A; color: #92400E; }
.info-box i, .warn-box i { margin-top: 2px; }
.warn-box--sm { padding: 8px 12px; font-size: 0.76rem; border-radius: 8px; }

/* Pestañas */
.tabs {
    display: inline-flex;
    gap: 4px;
    padding: 4px;
    background: #F1F5F9;
    border-radius: 12px;
}

.tabs__item {
    background: none;
    border: none;
    border-radius: 9px;
    padding: 7px 14px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #64748B;
}

.tabs__item--activa { background: #fff; color: #4C1D95; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); }

/* Tarjeta y tabla */
.crm-card {
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: var(--crm-card-shadow);
    overflow: hidden;
}

.toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #F1F5F9;
}

.toolbar__buscar { max-width: 280px; }

.tabla {
    width: 100%;
    border-collapse: collapse;
}

.tabla.is-loading { opacity: 0.6; }

.tabla th {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748B;
    font-weight: 700;
    padding: 10px 16px;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
}

.tabla td {
    padding: 12px 16px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
    color: #334155;
}

.tabla tbody tr:last-child td { border-bottom: none; }

.tabla__hint {
    text-align: center;
    font-size: 0.8rem;
    color: #94A3B8;
}

.rol__nombre { font-size: 0.85rem; font-weight: 600; color: #1E293B; }

.rol__desc {
    font-size: 0.74rem;
    color: #64748B;
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rol__slug { font-size: 0.7rem; color: #7C3AED; }

.acciones-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 520px;
}

.icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    background: #fff;
    color: #475569;
    font-size: 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.icon-btn:hover { background: #F1F5F9; }

/* Matriz */
.modulo-row {
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 8px;
}

.modulo-row__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.modulo-row__title { font-size: 0.84rem; font-weight: 700; color: #1E293B; }

.modulo-row__acciones,
.ofrecidas-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ofrecidas-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); }

.accion-check {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 0;
    padding: 5px 10px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-size: 0.78rem;
    color: #475569;
    cursor: pointer;
    user-select: none;
}

.accion-check--on { background: #F5F3FF; border-color: #C4B5FD; color: #4C1D95; font-weight: 600; }
.accion-check code { color: #94A3B8; font-weight: 400; }

.mini-badge {
    font-size: 0.66rem;
    font-weight: 600;
    color: #64748B;
    background: #F1F5F9;
    border-radius: 6px;
    padding: 1px 6px;
}

/* Modales */
.padre-box {
    padding: 12px 14px;
    border-radius: 10px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
}

.link-btn {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.76rem;
    font-weight: 600;
    color: #2563EB;
}

.form-error {
    padding: 8px 12px;
    border-radius: 8px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    color: #B91C1C;
    font-size: 0.8rem;
}

.chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #F1F5F9;
    color: #475569;
}

.chip--ok     { background: #ECFDF5; color: #047857; }
.chip--info   { background: #F5F3FF; color: #6D28D9; }
.chip--danger { background: #FEF2F2; color: #B91C1C; }
.chip--muted  { background: #F1F5F9; color: #64748B; }

@media (max-width: 576px) {
    .toolbar { flex-wrap: wrap; }
    .toolbar__buscar { max-width: none; }
}
</style>
